import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  UserPool,
  UserPoolsResponse,
  MyPoolsResponse,
  UserPoolStats,
  UserPoolFilters,
  GroupedUserPoolsResponse,
  ENVIRONMENT
} from 'shared';

@Injectable({
  providedIn: 'root'
})
export class UserPoolService {
  private readonly apiUrl = this.environment.apiUrl || 'http://localhost:3000/api';

  constructor(private http: HttpClient, @Inject(ENVIRONMENT) private environment: any) {}

  /**
   * Get paginated and filtered list of user pools
   */
  getUserPools(filters?: UserPoolFilters): Observable<UserPoolsResponse> {
    let params = new HttpParams();

    if (filters) {
      if (filters.filter_user_id) {
        params = params.set('filter_user_id', filters.filter_user_id.toString());
      }
      if (filters.filter_pool_id) {
        params = params.set('filter_pool_id', filters.filter_pool_id.toString());
      }
      if (filters.filter_order_id) {
        params = params.set('filter_order_id', filters.filter_order_id.toString());
      }
      if (filters.filter_balance_min !== undefined) {
        params = params.set('filter_balance_min', filters.filter_balance_min.toString());
      }
      if (filters.filter_balance_max !== undefined) {
        params = params.set('filter_balance_max', filters.filter_balance_max.toString());
      }
      if (filters.filter_turnover_min !== undefined) {
        params = params.set('filter_turnover_min', filters.filter_turnover_min.toString());
      }
      if (filters.filter_turnover_max !== undefined) {
        params = params.set('filter_turnover_max', filters.filter_turnover_max.toString());
      }
      if (filters.filter_user_search) {
        params = params.set('filter_user_search', filters.filter_user_search);
      }
      if (filters.sort_column) {
        params = params.set('sort_column', filters.sort_column);
      }
      if (filters.sort_direction) {
        params = params.set('sort_direction', filters.sort_direction);
      }
      if (filters.page) {
        params = params.set('page', filters.page.toString());
      }
      if (filters.per_page) {
        params = params.set('per_page', filters.per_page.toString());
      }
    }

    return this.http.get<UserPoolsResponse>(`${this.apiUrl}/user_pools`, { params });
  }

  /**
   * Get single user pool by ID
   */
  getUserPool(id: number): Observable<UserPool> {
    return this.http.get<UserPool>(`${this.apiUrl}/user_pools/${id}`);
  }

  /**
   * Get all user pools for a specific user
   */
  getUserPoolsByUser(userId: number): Observable<{ user_pools: UserPool[] }> {
    return this.http.get<{ user_pools: UserPool[] }>(
      `${this.apiUrl}/user_pools/by_user/${userId}`
    );
  }

  /**
   * Get user pool statistics
   */
  getStats(): Observable<UserPoolStats> {
    return this.http.get<UserPoolStats>(`${this.apiUrl}/user_pools/stats`);
  }

  /**
   * Get current user's pools (uses backend to determine current user from JWT)
   */
  getMyPools(): Observable<MyPoolsResponse> {
    return this.http.get<MyPoolsResponse>(
      `${this.apiUrl}/user_pools/my_pools`
    );
  }

  /**
   * Get grouped user pools (aggregated by user and pool)
   */
  getGroupedUserPools(filters?: UserPoolFilters): Observable<GroupedUserPoolsResponse> {
    let params = new HttpParams();

    if (filters) {
      if (filters.filter_user_id) {
        params = params.set('filter_user_id', filters.filter_user_id.toString());
      }
      if (filters.filter_pool_id) {
        params = params.set('filter_pool_id', filters.filter_pool_id.toString());
      }
      if (filters.filter_user_search) {
        params = params.set('filter_user_search', filters.filter_user_search);
      }
      if (filters.page) {
        params = params.set('page', filters.page.toString());
      }
      if (filters.per_page) {
        params = params.set('per_page', filters.per_page.toString());
      }
    }

    return this.http.get<GroupedUserPoolsResponse>(`${this.apiUrl}/user_pools/grouped`, { params });
  }
}
